import React, { useState } from "react";
import { Brain, Lock, Mail, ArrowRight, KeyRound, ArrowLeft } from "lucide-react";
import { authLogin, authForgot, formatApiErrorDetail } from "@/lib/auth_api";

const C = { bg: "#0B0F14", panel: "#0F1620", border: "#1B2A3A", text: "#E6EDF6", sub: "#A7B4C6", dim: "#6F7D92", accent: "#2EE6D6", bear: "#FF5C6C", bull: "#2BE38B" };

const Field = ({ icon: Icon, ...props }) => (
    <div className="flex items-center gap-2 rounded-lg px-3" style={{ background: C.bg, border: `1px solid ${C.border}` }}>
        <Icon size={16} color={C.dim} />
        <input {...props} className="flex-1 bg-transparent py-3 text-sm outline-none" style={{ color: C.text }} />
    </div>
);

export default function LoginPage({ onAuthed }) {
    const [mode, setMode] = useState("login"); // login | forgot
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState("");
    const [notice, setNotice] = useState("");

    const submitLogin = async (e) => {
        e.preventDefault();
        setBusy(true); setError(""); setNotice("");
        try {
            const u = await authLogin(email, password);
            onAuthed(u);
        } catch (err) {
            setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
        } finally { setBusy(false); }
    };

    const submitForgot = async (e) => {
        e.preventDefault();
        setBusy(true); setError(""); setNotice("");
        try {
            const r = await authForgot(email);
            setNotice(r.message || "If an account exists, a reset link has been emailed.");
        } catch (err) {
            setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
        } finally { setBusy(false); }
    };

    return (
        <div className="min-h-screen flex items-center justify-center px-4" style={{ background: C.bg, color: C.text }}>
            <div className="w-full max-w-md">
                <div className="flex items-center justify-center gap-2 mb-6">
                    <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: "rgba(46,230,214,0.12)", border: `1px solid ${C.accent}40` }}>
                        <Brain size={20} color={C.accent} />
                    </div>
                    <div className="text-lg font-semibold tracking-wide">GridDesk</div>
                </div>
                <div className="panel p-7 rounded-xl" style={{ background: C.panel, border: `1px solid ${C.border}` }} data-testid="login-card">
                    <div className="text-xl font-semibold mb-1">{mode === "login" ? "Sign in" : "Reset password"}</div>
                    <div className="text-sm mb-5" style={{ color: C.sub }}>
                        {mode === "login" ? "Enter your credentials to access the trading dashboard." : "We'll email you a secure reset link."}
                    </div>

                    {error && <div className="mb-3 text-sm rounded-md px-3 py-2" style={{ background: "rgba(255,92,108,0.1)", color: C.bear, border: `1px solid ${C.bear}40` }} data-testid="login-error">{error}</div>}
                    {notice && <div className="mb-3 text-sm rounded-md px-3 py-2" style={{ background: "rgba(43,227,139,0.1)", color: C.bull, border: `1px solid ${C.bull}40` }} data-testid="login-notice">{notice}</div>}

                    {mode === "login" ? (
                        <form onSubmit={submitLogin} className="space-y-3">
                            <Field icon={Mail} type="email" placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="login-email" />
                            <Field icon={Lock} type="password" placeholder="Password" value={password} onChange={(e) => setPassword(e.target.value)} required data-testid="login-password" />
                            <button type="submit" disabled={busy} className="btn-anim w-full flex items-center justify-center gap-2 rounded-lg py-3 text-sm font-semibold" style={{ background: C.accent, color: C.bg, opacity: busy ? 0.6 : 1 }} data-testid="login-submit">
                                {busy ? "Signing in…" : <>Sign in <ArrowRight size={16} /></>}
                            </button>
                            <button type="button" onClick={() => { setMode("forgot"); setError(""); setNotice(""); }} className="w-full text-center text-xs pt-1" style={{ color: C.dim }} data-testid="login-forgot-link">
                                Forgot password?
                            </button>
                        </form>
                    ) : (
                        <form onSubmit={submitForgot} className="space-y-3">
                            <Field icon={Mail} type="email" placeholder="Account email" value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="forgot-email" />
                            <button type="submit" disabled={busy} className="btn-anim w-full flex items-center justify-center gap-2 rounded-lg py-3 text-sm font-semibold" style={{ background: C.accent, color: C.bg, opacity: busy ? 0.6 : 1 }} data-testid="forgot-submit">
                                {busy ? "Sending…" : <><KeyRound size={16} /> Email reset link</>}
                            </button>
                            <button type="button" onClick={() => { setMode("login"); setError(""); setNotice(""); }} className="w-full flex items-center justify-center gap-1 text-xs pt-1" style={{ color: C.dim }} data-testid="forgot-back">
                                <ArrowLeft size={12} /> Back to sign in
                            </button>
                        </form>
                    )}
                </div>
                <div className="text-center text-[10px] uppercase tracking-[0.18em] mt-5" style={{ color: "#3A4759" }}>
                    AsterDex BTCUSDT · Dynamic Self-Healing Trailing Grid
                </div>
            </div>
        </div>
    );
}
